/**
 * ALTIORA · useUserLocation — hook de géolocalisation à cascade.
 *
 * Résout la localisation courante de l'utilisateur avec la stratégie suivante :
 *  1. Cache localStorage (TTL 24 h) — instantané
 *  2. `navigator.geolocation` silencieux — sans forcer de prompt intrusif
 *  3. Fallback via `/api/geo/default-location` — piloté par l'admin
 *  4. Reverse-geocoding via Nominatim OSM (fournisseur remplaçable, cf. `_reverseGeocode`)
 *
 * Retourne : { label, city, region, country_code, lat, lng, source, loading, refetch }
 *
 * Positionnement architectural : le fournisseur de reverse-geocode est isolé
 * dans une seule fonction interne — il suffit de la remplacer pour changer
 * de provider (Google Maps, Mapbox, HERE…) sans toucher au reste.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const CACHE_KEY = "altiora_user_location_v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 h

// ── Provider swap point ─────────────────────────────────────────────
// Remplacer cette fonction pour changer de fournisseur de reverse-geocode.
async function _reverseGeocode(lat, lng) {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=10&accept-language=fr`;
    const res = await fetch(url, { headers: { "Accept": "application/json" } });
    if (!res.ok) return null;
    const data = await res.json();
    const addr = data.address || {};
    const city = addr.city || addr.town || addr.village || addr.municipality || addr.county || "";
    const region = addr.state || addr.region || "";
    const country_code = (addr.country_code || "").toUpperCase();
    if (!city) return null;
    const label = region ? `${city}, ${region}` : city;
    return { label, city, region, country_code, lat, lng };
  } catch {
    return null;
  }
}

function _readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.value;
  } catch { return null; }
}

function _writeCache(value) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), value }));
  } catch { /* noop */ }
}

async function _getBrowserPosition(timeoutMs = 6000) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    // Vérifie l'état de la permission — n'invoque `getCurrentPosition`
    // que si l'utilisateur a déjà accepté (pas de prompt intempestif).
    const askIfGranted = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { timeout: timeoutMs, maximumAge: 5 * 60 * 1000 },
      );
    };
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "geolocation" })
        .then((s) => (s.state === "granted" ? askIfGranted() : resolve(null)))
        .catch(() => resolve(null));
    } else {
      resolve(null);
    }
  });
}

async function _fetchDefault() {
  try {
    const { data } = await api.get("/geo-config/default-location");
    return data;
  } catch {
    return null;
  }
}

export function useUserLocation({ eager = true } = {}) {
  const [state, setState] = useState(() => {
    const cached = _readCache();
    return cached
      ? { ...cached, loading: false }
      : { label: null, city: null, region: null, country_code: null,
          lat: null, lng: null, source: null, loading: true };
  });

  const resolve = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    // 1. tenter position navigateur (silencieux — pas de prompt)
    const browser = await _getBrowserPosition();
    if (browser) {
      const geo = await _reverseGeocode(browser.lat, browser.lng);
      if (geo) {
        const value = { ...geo, source: "browser" };
        _writeCache(value);
        setState({ ...value, loading: false });
        return value;
      }
    }
    // 2. fallback admin config
    const def = await _fetchDefault();
    if (def) {
      const value = { ...def, source: "admin-default" };
      _writeCache(value);
      setState({ ...value, loading: false });
      return value;
    }
    // 3. dernier recours — état vide, l'appelant décide
    setState((s) => ({ ...s, source: "unknown", loading: false }));
    return null;
  }, []);

  useEffect(() => {
    if (!eager) return;
    // Si on a déjà un cache frais, ne re-résout que si la source est inconnue
    if (state.source && state.label) return;
    resolve();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eager]);

  const clearCache = useCallback(() => {
    try { localStorage.removeItem(CACHE_KEY); } catch { /* noop */ }
  }, []);

  return useMemo(() => ({
    ...state,
    refetch: resolve,
    clearCache,
  }), [state, resolve, clearCache]);
}

/**
 * useDefaultLocation — variante légère qui n'interroge PAS le navigateur.
 * Utile pour les surfaces publiques (Landing, Login) où l'on veut uniquement
 * la valeur configurée en admin, sans jamais déclencher de prompt.
 */
export function useDefaultLocation() {
  const [state, setState] = useState(() => {
    const cached = _readCache();
    return cached
      ? { ...cached, loading: false, source: cached.source || "cache" }
      : { label: null, city: null, region: null, country_code: null,
          lat: null, lng: null, source: null, loading: true };
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const def = await _fetchDefault();
      if (cancelled) return;
      if (def) {
        const value = { ...def, source: "admin-default" };
        _writeCache(value);
        setState({ ...value, loading: false });
      } else {
        setState((s) => ({ ...s, source: "unknown", loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return state;
}
